/**
 * The "offline ready" status pill in the top bar.
 *
 * Tells the user when it is safe to go offline (precache complete) and reflects
 * live connectivity. Driven by custom events dispatched from `pwa.ts`.
 */

type State = 'checking' | 'downloading' | 'ready' | 'offline' | 'unsupported';

const LABELS: Record<State, string> = {
  checking: 'בודק…',
  downloading: 'מוריד למצב לא־מקוון…',
  ready: 'מוכן למצב לא־מקוון ✓',
  offline: 'לא מקוון · פועל מהמטמון',
  unsupported: 'ללא מצב לא־מקוון',
};

export function initOfflineIndicator(button: HTMLElement): void {
  const labelEl = button.querySelector('.label');
  let ready = false;

  const paint = (state: State): void => {
    button.dataset.state = state;
    if (labelEl) labelEl.textContent = LABELS[state];
  };

  const refresh = (): void => {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      paint('offline');
    } else {
      paint(ready ? 'ready' : 'downloading');
    }
  };

  // Show the offline state immediately if the app cold-starts without network
  // (a returning, already-cached user); otherwise stay on "checking" until the
  // service worker reports readiness.
  paint(typeof navigator !== 'undefined' && navigator.onLine === false ? 'offline' : 'checking');

  window.addEventListener('pwa:downloading', () => {
    if (!ready) paint('downloading');
  });
  window.addEventListener('pwa:offline-ready', () => {
    ready = true;
    refresh();
  });
  window.addEventListener('pwa:unsupported', () => paint('unsupported'));

  window.addEventListener('online', refresh);
  window.addEventListener('offline', refresh);
}
